import { useConsentStore, type ConsentState } from '~/stores/consent'
import { pushToDataLayer } from '~/utils/gtm'
import { sanitizeUrl, type HashedUserData } from '~/utils/tracking-pii'

export type SiteEnv = 'production' | 'staging'

export interface TrackOptions {
  /** GA4 ecommerce object; the previous one is cleared from the dataLayer first. */
  ecommerce?: Record<string, unknown>
  /** Hashed email/phone for Enhanced Conversions; dropped unless consent is accepted. */
  userData?: HashedUserData | null
  /** Defaults to a new UUID. Purchases pass the order code. */
  eventId?: string
}

export interface TrackContext {
  siteEnv: SiteEnv
  consent: ConsentState
  eventId: string
}

/** Events that also go to Meta CAPI through /_t/meta, with the same event_id as the pixel. */
export const META_BEACON_EVENTS: Record<string, 'AddToCart' | 'InitiateCheckout'> = {
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
}

/**
 * Parameters that belong to one event only. GTM keeps every pushed key in its data model,
 * so each push resets them; otherwise a search_term or checkout_type would ride along on
 * every later GA4 event.
 */
export const EVENT_SCOPED_KEYS = [
  'item_list_name', 'checkout_type', 'order_source', 'lead_type', 'service_fee',
  'deposit_amount', 'order_total', 'search_term', 'results_count', 'user_data',
] as const

export function buildTrackPayload(
  event: string,
  params: Record<string, unknown>,
  options: TrackOptions,
  context: TrackContext,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    ...Object.fromEntries(EVENT_SCOPED_KEYS.map(key => [key, undefined])),
    ...params,
    event,
    event_id: context.eventId,
    site_env: context.siteEnv,
  }
  // dataLayer merges objects: without the explicit null a click or page_view would
  // carry the previous event's products into GA4 and the pixel.
  payload.ecommerce = options.ecommerce ?? null
  if (context.consent === 'accepted' && options.userData && Object.keys(options.userData).length) {
    payload.user_data = options.userData
  }
  return payload
}

export function getSiteEnv(): SiteEnv {
  return useRuntimeConfig().public.siteEnv === 'production' ? 'production' : 'staging'
}

export function getConsentState(): ConsentState {
  return useConsentStore().state
}

export function newEventId(): string {
  return crypto.randomUUID()
}

function sendMetaBeacon(eventName: string, eventId: string, ecommerce: Record<string, unknown> | undefined) {
  const items = (ecommerce?.items as Array<{ item_id?: string }> | undefined) ?? []
  const body = JSON.stringify({
    event_name: eventName,
    event_id: eventId,
    consent: 'accepted',
    value: typeof ecommerce?.value === 'number' ? ecommerce.value : 0,
    currency: 'EUR',
    content_ids: items.map(item => String(item.item_id ?? '')).filter(Boolean),
    page_url: sanitizeUrl(window.location.href),
  })
  // text/plain: sendBeacon only accepts CORS-safelisted content types in Chrome.
  try {
    const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })
    if (navigator.sendBeacon?.('/_t/meta', blob)) return
  }
  catch {
    // fall through to fetch
  }
  fetch('/_t/meta', { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain;charset=UTF-8' } })
    .catch(() => {})
}

/**
 * The only way the site reports an analytics event. Returns the event_id, which is the
 * deduplication key between the Meta pixel (via GTM) and the Conversions API.
 * Attribution is never added here: it goes only to our own server with the forms.
 */
export function track(event: string, params: Record<string, unknown> = {}, options: TrackOptions = {}): string {
  const eventId = options.eventId ?? newEventId()
  if (import.meta.server) return eventId

  const consent = getConsentState()
  if (options.ecommerce) pushToDataLayer({ ecommerce: null })
  pushToDataLayer(buildTrackPayload(event, params, options, { siteEnv: getSiteEnv(), consent, eventId }))

  const metaEvent = META_BEACON_EVENTS[event]
  if (metaEvent && consent === 'accepted') sendMetaBeacon(metaEvent, eventId, options.ecommerce)

  return eventId
}
